import React, { useState } from 'react';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
  EmailIcon,
  FacebookIcon,
  LinkedinIcon,
  TwitterIcon,
} from 'react-share';
import { Button } from '@/components/shared/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shared/card';
import { Copy, Check } from 'lucide-react';

interface ShareControlsProps {
  shareUrl: string;
  title: string;
  summary: string;
}

export const ShareControls: React.FC<ShareControlsProps> = ({ shareUrl, title, summary }) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedLinkedinText, setCopiedLinkedinText] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      },
      (err) => {
        console.error('Could not copy link: ', err);
      }
    );
  };

  const handleLinkedInClick = () => {
    navigator.clipboard.writeText(summary).then(
      () => {
        setCopiedLinkedinText(true);
        setTimeout(() => setCopiedLinkedinText(false), 3000); // Give user a bit more time to see this
      },
      (err) => {
        console.error('Could not copy summary: ', err);
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Share Your Benchmarking Results with Others!</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-center space-x-2 sm:space-x-4">
          <div className="flex flex-col items-center">
            <TwitterShareButton url={shareUrl} title={title}>
              <TwitterIcon size={32} round />
            </TwitterShareButton>
            <span className="text-xs mt-1 text-gray-500">Twitter</span>
          </div>

          <div className="flex flex-col items-center">
            <div onClick={handleLinkedInClick} className="cursor-pointer">
              <LinkedinShareButton url={shareUrl}>
                <LinkedinIcon size={32} round />
              </LinkedinShareButton>
            </div>
            {copiedLinkedinText ? (
              <span className="text-xs mt-1 text-green-600 flex items-center">
                <Check size={12} className="mr-1" /> Copied!
              </span>
            ) : (
              <span className="text-xs mt-1 text-gray-500">LinkedIn</span>
            )}
          </div>

          <div className="flex flex-col items-center">
            <FacebookShareButton url={shareUrl} hashtag={"#PermitBenchmarking"}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
            <span className="text-xs mt-1 text-gray-500">Facebook</span>
          </div>

          <div className="flex flex-col items-center">
            <EmailShareButton url={shareUrl} subject={title} body={summary}>
              <EmailIcon size={32} round />
            </EmailShareButton>
            <span className="text-xs mt-1 text-gray-500">Email</span>
          </div>

          <div className="flex flex-col items-center">
            <Button
              variant="outline"
              size="icon"
              onClick={handleCopyLink}
              className="rounded-full w-8 h-8"
            >
              {copiedLink ? <Check size={16} /> : <Copy size={16} />}
            </Button>
            <span className="text-xs mt-1 text-gray-500">Copy</span>
          </div>
        </div>
        {copiedLinkedinText && (
          <p className="text-center text-sm text-gray-600 mt-4">
            We've copied the summary to your clipboard for you to paste on LinkedIn!
          </p>
        )}
      </CardContent>
    </Card>
  );
}; 